import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../api/apiConfig';

const AuthContext = createContext(null);
const AUTH_CONFIG_RETRY_COUNT = 20;
const AUTH_CONFIG_RETRY_DELAY_MS = 500;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchAuthConfig() {
  for (let attempt = 1; attempt <= AUTH_CONFIG_RETRY_COUNT; attempt += 1) {
    try {
      const response = await fetch(apiUrl('/auth/config'), { credentials: 'include' });
      if (response.ok) {
        return response.json();
      }
    } catch {
      // The backend may still be starting when the frontend opens.
    }

    if (attempt < AUTH_CONFIG_RETRY_COUNT) {
      await delay(AUTH_CONFIG_RETRY_DELAY_MS);
    }
  }

  return { enabled: true };
}

export function AuthProvider({ children }) {
  const [authEnabled, setAuthEnabled] = useState(undefined);
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauthenticated

  function updateUser(nextUser) {
    setUser(nextUser);
  }

  useEffect(() => {
    let cancelled = false;

    async function loadAuthState() {
      try {
        const config = await fetchAuthConfig();
        const enabled = config.enabled === true;

        if (cancelled) {
          return;
        }

        setAuthEnabled(enabled);

        if (!enabled) {
          updateUser(null);
          return;
        }

        const userResponse = await fetch(apiUrl('/auth/me'), { credentials: 'include' });

        if (cancelled) {
          return;
        }

        if (userResponse.status === 401) {
          updateUser(null);
          return;
        }

        try {
          const parsed = await userResponse.json();
          // Successful re-auth: clear any stale logout signal so AuthGuard
          // won't redirect back to /signed-out on the next navigation.
          localStorage.removeItem('app.logout');
          updateUser(parsed);
        } catch (e) {
          console.error("Failed to parse user auth response. This usually happens when not authenticated and the server returns an HTML page instead of a 401 status.", e);
          const responseText = await userResponse.text();
          console.error("Server response:", responseText);
          updateUser(null);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load auth state", e);
          setAuthEnabled(true);
          updateUser(null);
        }
      }
    }

    loadAuthState();

    return () => {
      cancelled = true;
    };
  }, []);

  // Clear auth state in this tab when another tab writes the logout signal.
  // The storage event only fires in tabs *other* than the one that triggered logout,
  // so this catches exactly the "logged out elsewhere" case.
  useEffect(() => {
    function handleStorage(e) {
      if (e.key === 'app.logout' && e.newValue !== null) {
        updateUser(null);
      }
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return <AuthContext.Provider value={{ authEnabled, user, setUser: updateUser }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  const authEnabled = ctx.authEnabled === true;

  return {
    user: ctx.user,
    setUser: ctx.setUser,
    isAuthEnabled: authEnabled,
    isLoading: ctx.authEnabled === undefined || (authEnabled && ctx.user === undefined),
    isAuthenticated: !authEnabled || (ctx.user !== null && ctx.user !== undefined),
  };
}
